import { useEffect, useState } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router';
import type { SectionDefinition } from '../sections/types';
import { sections } from '../sections/registry';
import { groupByCategory } from '../sections/groupByCategory';
import { useHubContext } from './HubContext';
import { MedullaMark } from './Logo';

/** What a routed page can read back via `useOutletContext<HubOutletContext>()`. */
export interface HubOutletContext {
  /** Sections the signed-in user can see, in registry order — the same filtering the sidebar uses. */
  visibleSections: SectionDefinition[];
}

/**
 * The hub's chrome: topbar, a collapsible sidebar grouping sections under categories (an
 * uncategorized section sits ungrouped, above any categories), and the routed page. Shared by the
 * Entra path and the local development path — the only difference between them is where the access
 * token comes from and whether a mailbox is reachable, both carried in `HubContext`.
 *
 * Whether a section is visible at all is decided here, from `GET /api/{id}/access` — advisory only.
 * The real boundary is each module's own backend authorization; this just keeps the sidebar and the
 * routed content from offering what the API would refuse anyway.
 */
export function HubShell() {
  const { api, userLabel, onSignOut } = useHubContext();
  const location = useLocation();

  const currentSection = sections.find(
    (s) => location.pathname === s.basePath || location.pathname.startsWith(`${s.basePath}/`),
  );

  const [access, setAccess] = useState<Record<string, boolean> | null>(null);
  const [openCategories, setOpenCategories] = useState<Set<string>>(() => new Set());
  const [openApps, setOpenApps] = useState<Set<string>>(() => new Set());

  // Keeps the active category/app open as navigation happens — including the very first render,
  // where the `/` -> default-section redirect hasn't resolved yet, so seeding this from a plain
  // useState initializer would capture the pre-redirect location instead of the one the user lands
  // on. Additive only: never closes anything the user opened by hand elsewhere.
  useEffect(() => {
    if (!currentSection) return;

    const category = currentSection.category;
    if (category !== null) {
      setOpenCategories((current) => (current.has(category) ? current : new Set(current).add(category)));
    }

    setOpenApps((current) => (current.has(currentSection.id) ? current : new Set(current).add(currentSection.id)));
  }, [currentSection]);

  useEffect(() => {
    let cancelled = false;

    Promise.all(
      sections
        // A client-only section has no API to ask, and the 404 would be caught below and read as a
        // refusal. Left out of the map entirely, so its id stays absent from `access` — which the
        // `!== false` tests below already treat as visible.
        .filter((section) => !section.clientOnly)
        .map((section) =>
          api
            .getSectionAccess(section.id)
            .then((result) => [section.id, result.allowed] as const)
            // A section whose access can't be determined is treated as not allowed — offering
            // something the API might refuse is worse than briefly hiding it.
            .catch(() => [section.id, false] as const),
        ),
    ).then((pairs) => {
      if (!cancelled) setAccess(Object.fromEntries(pairs));
    });

    return () => {
      cancelled = true;
    };
  }, [api]);

  function toggleCategory(category: string) {
    setOpenCategories((current) => toggled(current, category));
  }

  function toggleApp(sectionId: string) {
    setOpenApps((current) => toggled(current, sectionId));
  }

  const visibleSections = access ? sections.filter((s) => access[s.id] !== false) : [];
  const uncategorized = visibleSections.filter((s) => s.category === null);
  const categories = groupByCategory(visibleSections);
  const currentAllowed = access === null || !currentSection || access[currentSection.id] !== false;

  return (
    <>
      <header className="topbar">
        <div className="topbar__brand">
          <MedullaMark />
          <span className="topbar__name">HR Hub</span>
        </div>

        <div className="topbar__account">
          <span className="topbar__user">{userLabel}</span>
          {onSignOut && (
            <button type="button" className="button button--quiet" onClick={onSignOut}>
              Sign out
            </button>
          )}
        </div>
      </header>

      <div className="hub-layout">
        <aside className="hub-sidebar" aria-label="Sections">
          <NavLink
            to="/"
            end
            className={({ isActive }) =>
              isActive ? 'hub-app__link hub-home-link hub-app__link--active' : 'hub-app__link hub-home-link'
            }
          >
            Home
          </NavLink>

          {access === null ? (
            <p className="hub-sidebar__status">Checking your access…</p>
          ) : (
            <>
              {uncategorized.map((section) => (
                <SidebarApp
                  key={section.id}
                  section={section}
                  isOpen={openApps.has(section.id)}
                  onToggle={() => toggleApp(section.id)}
                />
              ))}

              {[...categories.entries()].map(([category, items]) => (
                <div className="hub-category" key={category}>
                  <button
                    type="button"
                    className="hub-category__header"
                    aria-expanded={openCategories.has(category)}
                    onClick={() => toggleCategory(category)}
                  >
                    <span
                      className={
                        openCategories.has(category)
                          ? 'hub-category__chevron hub-category__chevron--open'
                          : 'hub-category__chevron'
                      }
                      aria-hidden="true"
                    />
                    {category}
                  </button>

                  {openCategories.has(category) && (
                    <div className="hub-category__body">
                      {items.map((section) => (
                        <SidebarApp
                          key={section.id}
                          section={section}
                          isOpen={openApps.has(section.id)}
                          onToggle={() => toggleApp(section.id)}
                        />
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </>
          )}
        </aside>

        <main className="hub-content">
          {access === null ? (
            <div className="page-message">Checking your access…</div>
          ) : !currentAllowed ? (
            <div className="page-message page-message--error">
              <h2>You don't have access to this section</h2>
              <p>Ask an administrator to add you to the group this section requires.</p>
            </div>
          ) : (
            <Outlet context={{ visibleSections } satisfies HubOutletContext} />
          )}
        </main>
      </div>
    </>
  );
}

/** One sidebar entry: a plain link when the section has one page, an expandable group otherwise. */
function SidebarApp({
  section,
  isOpen,
  onToggle,
}: {
  section: SectionDefinition;
  isOpen: boolean;
  onToggle: () => void;
}) {
  // Detail pages are routed but not listed: they are reached from a row, not from the nav.
  const navPages = section.pages.filter((page) => !page.hidden);

  if (navPages.length === 1) {
    return (
      <NavLink
        to={section.basePath}
        className={({ isActive }) => (isActive ? 'hub-app__link hub-app__link--active' : 'hub-app__link')}
      >
        {section.navLabel}
      </NavLink>
    );
  }

  return (
    <div className="hub-app">
      <button type="button" className="hub-app__header" aria-expanded={isOpen} onClick={onToggle}>
        <span className={isOpen ? 'hub-app__chevron hub-app__chevron--open' : 'hub-app__chevron'} aria-hidden="true" />
        {section.navLabel}
      </button>

      {isOpen && (
        <div className="hub-app__pages">
          {navPages.map((page) => (
            <NavLink
              key={page.id}
              to={page.path === '' ? section.basePath : `${section.basePath}/${page.path}`}
              end
              className={({ isActive }) => (isActive ? 'hub-app__link hub-app__link--active' : 'hub-app__link')}
            >
              {page.navLabel}
            </NavLink>
          ))}
        </div>
      )}
    </div>
  );
}

function toggled(current: Set<string>, key: string): Set<string> {
  const next = new Set(current);
  if (next.has(key)) {
    next.delete(key);
  } else {
    next.add(key);
  }
  return next;
}
